import { useEffect, useState, useRef } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { X, DownloadSimple, UploadSimple, CaretRight, Gear, ArrowLeft } from "@phosphor-icons/react";

const MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const rupiah = (n) => `Rp ${(Number(n) || 0).toLocaleString("id-ID")},-`;
const stColor = (st) => (st === "Lunas" ? "bg-green-500 text-white" : st === "Sebagian" ? "bg-yellow-400 text-yellow-900" : "bg-red-500 text-white");

export default function SppReport() {
  const { user } = useAuth();
  const isSD = user?.school === "sdqu";
  const logo = isSD ? "/sdqu_logo.png" : "/tk_logo.png";
  const now = new Date();
  const [month, setMonth] = useState(MONTHS[now.getMonth()]);
  const [year, setYear] = useState(now.getFullYear());
  const [summary, setSummary] = useState(null);
  const [nav, setNav] = useState({ level: "summary", group: null, kelas: null });
  const [detail, setDetail] = useState(null);
  const [defAmt, setDefAmt] = useState(isSD ? 500000 : 400000);
  const [settingOpen, setSettingOpen] = useState(false);
  const [newAmt, setNewAmt] = useState("");
  const [preview, setPreview] = useState(null); // {student, list}
  const [importPrev, setImportPrev] = useState(null);
  const fileRef = useRef();
  const years = [now.getFullYear() - 2, now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];

  const loadSummary = () => api.get(`/admin/spp/summary?month=${month}&year=${year}`).then((r) => { setSummary(r.data); setDefAmt(r.data.default_amount); });
  useEffect(() => { loadSummary(); setNav({ level: "summary", group: null, kelas: null }); /* eslint-disable-next-line */ }, [month, year]);

  const openDetail = (kelas, group) => {
    api.get(`/admin/spp/detail?kelas=${encodeURIComponent(kelas)}&month=${month}&year=${year}`)
      .then((r) => { setDetail(r.data); setNav({ level: "detail", group: group ?? nav.group, kelas }); });
  };
  const clickGroup = (g) => {
    if (isSD) setNav({ level: "subs", group: g.group, kelas: null });
    else openDetail(g.group, g.group);
  };

  const dl = async (url, name) => {
    try { const res = await api.get(url, { responseType: "blob" }); const u = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = u; a.download = name; a.click(); URL.revokeObjectURL(u); }
    catch (e) { toast.error("Gagal export"); }
  };
  const importFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post(`/admin/spp/import-preview?kelas=${encodeURIComponent(nav.kelas || "")}`, fd);
      setImportPrev({ ...data, file: f, kelas: nav.kelas });
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    e.target.value = "";
  };
  const confirmImport = async () => {
    const fd = new FormData(); fd.append("file", importPrev.file);
    try {
      const { data } = await api.post(`/admin/spp/import?kelas=${encodeURIComponent(importPrev.kelas || "")}`, fd);
      toast.success(`Import: ${data.created} baru, ${data.updated} diperbarui`);
      setImportPrev(null); openDetail(importPrev.kelas);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const saveDefault = async () => {
    try { await api.put("/admin/spp/default-amount", { amount: Number(newAmt) }); setDefAmt(Number(newAmt)); setSettingOpen(false); loadSummary(); toast.success("Default SPP disimpan"); }
    catch (e) { toast.error("Gagal menyimpan"); }
  };
  const openPreview = async (row) => {
    try { const { data } = await api.get(`/spp?student_id=${row.id}`); setPreview({ student: row, list: data }); }
    catch (e) { toast.error("Gagal memuat"); }
  };

  const subsOfGroup = summary?.groups?.find((g) => g.group === nav.group)?.subclasses || [];

  return (
    <div>
      <PageHeader title={`Laporan SPP ${isSD ? "SD" : "TK"}`} />
      <div className="px-5 pt-4 pb-10">
        {/* Filters */}
        <div className="flex items-center gap-2 mb-4">
          <select data-testid="spp-report-month" value={month} onChange={(e) => setMonth(e.target.value)}
            className="flex-1 bg-white border border-stone-200 rounded-xl py-2.5 px-3 outline-none focus:border-primary text-sm font-semibold">
            {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <select data-testid="spp-report-year" value={year} onChange={(e) => setYear(Number(e.target.value))}
            className="w-28 bg-white border border-stone-200 rounded-xl py-2.5 px-3 outline-none focus:border-primary text-sm font-semibold">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button data-testid="spp-default-setting-btn" onClick={() => { setNewAmt(String(defAmt)); setSettingOpen(true); }}
            className="w-11 h-11 shrink-0 rounded-xl bg-primary/10 text-primary inline-flex items-center justify-center active:scale-90 transition-transform"><Gear size={20} weight="bold" /></button>
        </div>

        <h2 className="font-heading font-bold text-lg text-stone-800 mb-3" data-testid="spp-report-title">
          Laporan SPP {isSD ? "SD" : "TK"} {month} {year}
        </h2>

        {/* SUMMARY */}
        {nav.level === "summary" && summary && (
          <>
            <div className="rounded-2xl overflow-x-auto border border-green-200 shadow-sm" data-testid="spp-summary-table">
              <table className="w-full border-collapse text-xs sm:text-sm min-w-[640px]">
                <thead>
                  <tr className="bg-green-800 text-white text-left">
                    <th className="px-3 py-2.5 font-bold whitespace-nowrap">Kelas</th>
                    <th className="px-2 py-2.5 font-bold text-center">Total Siswa</th>
                    <th className="px-2 py-2.5 font-bold text-center">Lunas</th>
                    <th className="px-2 py-2.5 font-bold text-center">Belum</th>
                    <th className="px-3 py-2.5 font-bold text-right whitespace-nowrap">Total Lunas</th>
                    <th className="px-3 py-2.5 font-bold text-right whitespace-nowrap">Total Belum</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.groups.map((g, i) => (
                    <tr key={g.group} data-testid={`spp-summary-row-${g.group}`} onClick={() => clickGroup(g)}
                      className={`cursor-pointer hover:bg-green-100 ${i % 2 === 0 ? "bg-green-50" : "bg-white"}`}>
                      <td className="px-3 py-2.5 font-semibold text-primary border-t border-green-100 whitespace-nowrap flex items-center gap-1">{g.group} <CaretRight size={13} weight="bold" /></td>
                      <td className="px-2 py-2.5 text-center border-t border-green-100">{g.total}</td>
                      <td className="px-2 py-2.5 text-center border-t border-green-100 text-green-700 font-bold">{g.lunas}</td>
                      <td className="px-2 py-2.5 text-center border-t border-green-100 text-red-600 font-bold">{g.belum}</td>
                      <td className="px-3 py-2.5 text-right border-t border-green-100 text-green-700 whitespace-nowrap">{rupiah(g.lunas_rp)}</td>
                      <td className="px-3 py-2.5 text-right border-t border-green-100 text-red-600 whitespace-nowrap">{rupiah(g.belum_rp)}</td>
                    </tr>
                  ))}
                  <tr className="bg-green-800 text-white font-bold" data-testid="spp-summary-total">
                    <td className="px-3 py-2.5">Total</td>
                    <td className="px-2 py-2.5 text-center">{summary.grand.total}</td>
                    <td className="px-2 py-2.5 text-center">{summary.grand.lunas}</td>
                    <td className="px-2 py-2.5 text-center">{summary.grand.belum}</td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">{rupiah(summary.grand.lunas_rp)}</td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">{rupiah(summary.grand.belum_rp)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <button data-testid="spp-export-all-btn" onClick={() => dl(`/admin/spp/export-all.xlsx?year=${year}`, `SPP_Akumulasi_${year}.xlsx`)}
              className="w-full mt-4 bg-green-700 text-white font-semibold py-3 rounded-2xl active:scale-95 transition-transform inline-flex items-center justify-center gap-2">
              <DownloadSimple size={18} weight="bold" /> Export Akumulasi Semua Kelas ({year})
            </button>
            <p className="text-center text-xs text-stone-400 mt-2">Klik nama kelas untuk melihat detail per murid</p>
          </>
        )}

        {/* SD SUB-CLASS LIST */}
        {nav.level === "subs" && (
          <>
            <button data-testid="spp-back-summary" onClick={() => setNav({ level: "summary", group: null, kelas: null })}
              className="text-primary text-sm font-semibold inline-flex items-center gap-1 mb-3"><ArrowLeft size={16} weight="bold" /> Kembali</button>
            <h3 className="font-heading font-bold text-stone-700 mb-2">{nav.group}</h3>
            <div className="rounded-2xl overflow-hidden border border-green-200 shadow-sm">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-green-800 text-white text-left">
                    <th className="px-3 py-2.5 font-bold">Sub Kelas</th>
                    <th className="px-2 py-2.5 font-bold text-center">Siswa</th>
                    <th className="px-2 py-2.5 font-bold text-center">Lunas</th>
                    <th className="px-2 py-2.5 font-bold text-center">Belum</th>
                  </tr>
                </thead>
                <tbody>
                  {subsOfGroup.map((s, i) => (
                    <tr key={s.kelas} data-testid={`spp-subclass-row-${s.kelas}`} onClick={() => openDetail(s.kelas, nav.group)}
                      className={`cursor-pointer hover:bg-green-100 ${i % 2 === 0 ? "bg-green-50" : "bg-white"}`}>
                      <td className="px-3 py-2.5 font-semibold text-primary border-t border-green-100 flex items-center gap-1">{s.kelas} <CaretRight size={13} weight="bold" /></td>
                      <td className="px-2 py-2.5 text-center border-t border-green-100">{s.total}</td>
                      <td className="px-2 py-2.5 text-center border-t border-green-100 text-green-700 font-bold">{s.lunas}</td>
                      <td className="px-2 py-2.5 text-center border-t border-green-100 text-red-600 font-bold">{s.belum}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* DETAIL PER CLASS */}
        {nav.level === "detail" && detail && (
          <>
            <button data-testid="spp-back-btn" onClick={() => (isSD ? setNav({ level: "subs", group: nav.group, kelas: null }) : setNav({ level: "summary", group: null, kelas: null }))}
              className="text-primary text-sm font-semibold inline-flex items-center gap-1 mb-3"><ArrowLeft size={16} weight="bold" /> Kembali</button>
            <h3 className="font-heading font-bold text-stone-700 mb-3" data-testid="spp-detail-title">Siswa {detail.kelas} - {month} {year}</h3>
            <div className="rounded-2xl overflow-x-auto border border-green-200 shadow-sm" data-testid="spp-detail-table">
              <table className="w-full border-collapse text-sm min-w-[440px]">
                <thead>
                  <tr className="bg-green-800 text-white text-left">
                    <th className="px-3 py-2.5 font-bold w-10">No</th>
                    <th className="px-3 py-2.5 font-bold">Nama Murid</th>
                    <th className="px-3 py-2.5 font-bold text-center whitespace-nowrap">SPP</th>
                    <th className="px-2 py-2.5 font-bold text-center">Status</th>
                    <th className="px-2 py-2.5 font-bold text-center">Lihat</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.rows.map((r, i) => (
                    <tr key={r.id} data-testid={`spp-detail-row-${i}`} className={i % 2 === 0 ? "bg-green-50" : "bg-white"}>
                      <td className="px-3 py-2.5 border-t border-green-100 text-stone-500">{i + 1}</td>
                      <td className="px-3 py-2.5 border-t border-green-100 font-semibold text-stone-700">{r.name}</td>
                      <td className="px-3 py-2.5 border-t border-green-100 text-center whitespace-nowrap">
                        <span className={`inline-block px-2.5 py-1 rounded-lg text-xs font-bold ${stColor(r.status)}`}>{rupiah(r.amount)}</span>
                      </td>
                      <td className="px-2 py-2.5 border-t border-green-100 text-center">
                        <span className={`text-xs font-bold ${r.status === "Lunas" ? "text-green-700" : "text-red-600"}`}>{r.status}</span>
                      </td>
                      <td className="px-2 py-2.5 border-t border-green-100 text-center">
                        <button data-testid={`spp-detail-view-${i}`} onClick={() => openPreview(r)} className="text-primary text-xs font-semibold underline">View</button>
                      </td>
                    </tr>
                  ))}
                  {detail.rows.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-stone-400">Belum ada murid di kelas ini.</td></tr>}
                  <tr className="bg-green-800 text-white font-bold">
                    <td className="px-3 py-2.5" colSpan={2}>Total</td>
                    <td className="px-3 py-2.5 text-center text-green-200 whitespace-nowrap" colSpan={3}>Lunas: {rupiah(detail.total_lunas)} &nbsp;|&nbsp; <span className="text-red-200">Belum: {rupiah(detail.total_belum)}</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-4">
              <button data-testid="spp-detail-export-btn" onClick={() => dl(`/admin/spp/export.xlsx?kelas=${encodeURIComponent(detail.kelas)}&month=${month}&year=${year}`, `SPP_${detail.kelas}_${month}_${year}.xlsx`)}
                className="bg-green-700 text-white font-semibold py-2.5 rounded-xl text-xs active:scale-95 transition-transform inline-flex items-center justify-center gap-1"><DownloadSimple size={15} weight="bold" /> Export</button>
              <button data-testid="spp-detail-template-btn" onClick={() => dl(`/admin/spp/import-template.xlsx?kelas=${encodeURIComponent(detail.kelas)}&month=${month}&year=${year}`, `Template_${detail.kelas}.xlsx`)}
                className="bg-stone-200 text-stone-700 font-semibold py-2.5 rounded-xl text-xs active:scale-95 transition-transform">Template</button>
              <button data-testid="spp-detail-import-btn" onClick={() => fileRef.current?.click()}
                className="bg-primary text-white font-semibold py-2.5 rounded-xl text-xs active:scale-95 transition-transform inline-flex items-center justify-center gap-1"><UploadSimple size={15} weight="bold" /> Import</button>
              <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={importFile} data-testid="spp-import-input" />
              {importPrev && (
                <div className="fixed inset-0 z-[70] bg-black/40 flex items-end md:items-center justify-center" onClick={() => setImportPrev(null)}>
                  <div onClick={(e) => e.stopPropagation()} className="bg-white w-full md:max-w-lg rounded-t-3xl md:rounded-3xl p-5 max-h-[85vh] overflow-y-auto" data-testid="spp-import-modal">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-heading font-bold text-lg text-stone-800">Pratinjau Import SPP</h3>
                      <button onClick={() => setImportPrev(null)}><X size={22} className="text-stone-400" /></button>
                    </div>
                    <p className="text-sm text-stone-500 mb-3">Total {importPrev.total} baris · <span className="text-green-600 font-semibold">{importPrev.valid} valid</span> · <span className="text-red-500 font-semibold">{importPrev.invalid} bermasalah</span></p>
                    <div className="rounded-xl border border-stone-200 overflow-x-auto mb-4">
                      <table className="w-full text-[11px] border-collapse min-w-[420px]">
                        <thead><tr className="bg-stone-100 text-left"><th className="px-2 py-1.5">Nama</th><th className="px-2 py-1.5">Bulan</th><th className="px-2 py-1.5">Status</th><th className="px-2 py-1.5">Nominal</th><th className="px-2 py-1.5">Ket</th></tr></thead>
                        <tbody>
                          {importPrev.rows.map((r, i) => (
                            <tr key={i} data-testid={`import-row-${i}`} className={r.error ? "bg-red-50" : "bg-white"}>
                              <td className="px-2 py-1 border-t border-stone-100">{r.name || "-"}</td>
                              <td className="px-2 py-1 border-t border-stone-100">{r.month || "?"} {r.year || ""}</td>
                              <td className="px-2 py-1 border-t border-stone-100">{r.status}</td>
                              <td className="px-2 py-1 border-t border-stone-100">{(r.amount || 0).toLocaleString("id-ID")}</td>
                              <td className="px-2 py-1 border-t border-stone-100 text-red-500">{r.error || "✓"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <button data-testid="spp-import-confirm" disabled={importPrev.valid === 0} onClick={confirmImport}
                      className="w-full bg-primary text-white font-semibold py-3 rounded-full active:scale-95 transition-transform disabled:opacity-50">Import {importPrev.valid} Data Valid</button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Default amount setting */}
      {settingOpen && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-end md:items-center justify-center" onClick={() => setSettingOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full md:max-w-sm rounded-t-3xl md:rounded-3xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg text-stone-800">Default SPP {isSD ? "SD" : "TK"}</h3>
              <button onClick={() => setSettingOpen(false)}><X size={22} className="text-stone-400" /></button>
            </div>
            <p className="text-xs text-stone-400 mb-1">Nominal SPP per bulan (Rp)</p>
            <input data-testid="spp-default-input" type="number" value={newAmt} onChange={(e) => setNewAmt(e.target.value)}
              className="w-full bg-stone-50 border border-stone-200 rounded-xl py-2.5 px-3 mb-4 outline-none focus:border-primary" />
            <button data-testid="spp-default-save" onClick={saveDefault}
              className="w-full bg-primary text-white font-semibold py-3.5 rounded-full active:scale-95 transition-transform">Simpan</button>
          </div>
        </div>
      )}

      {/* Parent-style preview with logo + watermark */}
      {preview && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-end md:items-center justify-center" onClick={() => setPreview(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-5 max-h-[90vh] overflow-y-auto" data-testid="spp-preview-modal">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <img src={logo} alt="logo" className="w-9 h-9 object-contain" data-testid="spp-preview-logo" />
                <div>
                  <p className="font-heading font-bold text-stone-800 leading-tight">{preview.student.name}</p>
                  <p className="text-stone-500 text-xs">{detail?.kelas} · {isSD ? "SDQu" : "TK"} Muslim Kids</p>
                </div>
              </div>
              <button onClick={() => setPreview(null)}><X size={22} className="text-stone-400" /></button>
            </div>
            <div className="relative rounded-2xl overflow-hidden border border-green-200 shadow-sm">
              <img src={logo} alt="" aria-hidden className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-52 h-52 object-contain z-10" style={{ opacity: 0.15 }} data-testid="spp-preview-watermark" />
              <table className="relative w-full border-collapse text-[11px]">
                <thead>
                  <tr className="bg-green-800 text-white text-left">
                    <th className="px-3 py-1.5 font-bold whitespace-nowrap">Bulan</th>
                    <th className="px-2 py-1.5 font-bold text-center">SPP</th>
                    <th className="px-2 py-1.5 font-bold text-center whitespace-nowrap">Tgl Bayar</th>
                    <th className="px-3 py-1.5 font-bold">Ket</th>
                  </tr>
                </thead>
                <tbody>
                  {MONTHS.map((m, i) => {
                    const bill = preview.list.find((b) => b.month === m && String(b.year) === String(year));
                    const st = bill?.status || "Belum";
                    return (
                      <tr key={m} className={i % 2 === 0 ? "bg-green-50/70" : "bg-white/70"}>
                        <td className="px-3 py-1 font-semibold text-stone-700 border-t border-green-100 whitespace-nowrap">{MONTHS_SHORT[i]} {String(year).slice(-2)}</td>
                        <td className="px-2 py-1 text-center border-t border-green-100 whitespace-nowrap">
                          <span className={`inline-block px-2 py-0.5 rounded-lg font-bold ${stColor(st)}`}>{rupiah(bill?.amount ?? defAmt)}</span>
                        </td>
                        <td className="px-2 py-1 text-center text-stone-500 border-t border-green-100 whitespace-nowrap">{bill?.paid_date ? new Date(bill.paid_date).toLocaleDateString("id-ID", { day: "numeric", month: "short" }) : "-"}</td>
                        <td className="px-3 py-1 text-stone-500 border-t border-green-100">{bill?.note || "-"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
